import { DateTrustCode } from '../common/types'
import { DigitalPresence } from '../digitalPresences/types'
import { FundingEvent } from '../fundingEvents/types'
import { FundingRound } from '../fundingRounds/types'
import { OrganizationEvent } from '../organizationEvents/types'
import { Person, Position } from '../people/types'
import { PerformanceIndicator } from '../performanceIndicators/types'
import { SignalAssessment } from '../signalAssessments/types'

export type OrganizationsState = {
  readonly isFetching: boolean
  readonly items: IdIndexedOrganizations
}

export type IdIndexedOrganizations = {
  readonly [organizationId: number]: Organization
}

export type Organization = {
  readonly id: number
  readonly name: string
  readonly types: OrganizationType[]
  readonly source: OrganizationSource
  readonly domain: string
  readonly blurb?: string
  readonly description?: string
  readonly foundedOn?: Date
  readonly foundedOnTrustCode?: DateTrustCode
  readonly insights?: string
  readonly locationsRelations?: OrganizationLocation[]
  readonly industriesRelations?: OrganizationIndustry[]
  readonly status?: OrganizationStatus
  readonly reportedLikelihoodFundraising?: Date | string
  readonly currentRoundType?: string
  readonly foundersFullTime?: boolean
  readonly foundersFullTimeExplanation?: string
  readonly experienceInIndustry?: boolean
  readonly experienceInIndustryExplanation?: string
  readonly serialEntrepreneurs?: boolean
  readonly serialEntrepreneursExplanation?: string
  readonly otherTractionMetrics?: string
  readonly digitalPresence?: DigitalPresence[]
  readonly fundingRounds?: FundingRound[]
  readonly fundingEvent?: FundingEvent
  readonly lastFundingEvent?: string
  readonly employees?: Person[]
  readonly referrers?: Referral[]
  readonly currentRoundAnnouncedOn?: Date | string
  readonly signalAssessments?: SignalAssessment[]
  readonly performanceIndicators?: PerformanceIndicator[]
  readonly events?: OrganizationEvent[]
  readonly rejectedInvestors?: string
  readonly investorsInCapTable?: string
  readonly lastRoundType?: string
  readonly totalFundingRaised?: number
  readonly lastRoundClosedOn?: Date | string
  readonly founderNotes?: string
  readonly foundersData?: FounderData[]
  readonly positions?: Position[]
  readonly releasedToNetworkManager?: boolean
  readonly communicateRoundClosed?: boolean
  readonly communicateRoundClosedDescription?: string
  readonly currentStageData?: { currentStage: string; isInferred: boolean }
  readonly balancedFoundingTeam?: boolean
  readonly foundersHaveOtherRelevantExperience?: boolean
  readonly techFounderWithRelevantDegree?: boolean
  readonly processedByOps?: boolean
}

export type FounderData = {
  linkedinUrl: string
  role: string
}

export type Referral = {
  value: string
  display: {
    label: string
  }
}

export type Extraction = {
  id: number
  organizationId: Organization['id']
  status: ExtractionStatus
  hasExtractedOrganization: boolean
  hasExtractedPeople: boolean
}

export enum ExtractionStatus {
  inProgress = 'inProgress',
  done = 'done',
  error = 'error',
}

export enum OrganizationType {
  company = 'Company',
  investor = 'Investor',
  school = 'School',
}

export const OrganizationTypeOptions = [
  {
    label: OrganizationType.company,
    value: OrganizationType.company,
  },
  {
    label: OrganizationType.investor,
    value: OrganizationType.investor,
  },
  {
    label: OrganizationType.school,
    value: OrganizationType.school,
  },
]

export enum OrganizationStatus {
  operating = 'Operating',
  acquired = 'Acquired',
  ipo = 'IPO',
  roundClosed = 'Round closed',
  roundPaused = 'Round paused',
  closed = 'Closed',
  unknown = 'Unknown',
}

export const OrganizationStatusOptions = Object.values(OrganizationStatus).map((status) => ({
  label: status,
  value: status,
}))

export enum OrganizationSource {
  inbound = 'Inbound',
  outbound = 'Outbound',
}

export const OrganizationSourceOptions = [
  {
    label: OrganizationSource.inbound,
    value: OrganizationSource.inbound,
  },
  {
    label: OrganizationSource.outbound,
    value: OrganizationSource.outbound,
  },
]

export enum LocationType {
  headquarters = 'headquarters',
  operating = 'operating',
}

export type OrganizationLocation = {
  readonly id: number
  readonly type: LocationType
  readonly location: Location
}

export type OrganizationLocationData = Omit<OrganizationLocation, 'id'>

export type Location = {
  readonly id: number
  readonly fullAddress: string
  readonly street?: string
  readonly number?: string
  readonly postalCode?: string
  readonly city?: string
  readonly region?: string
  readonly countryCode: string
}

export type OrganizationIndustry = {
  readonly id: number
  readonly industry: Industry
}

export type OrganizationIndustryData = Omit<OrganizationIndustry, 'id'>

export type Industry = {
  readonly id: number
  readonly isInOurTaxonomy: boolean
  readonly name: string
  readonly type: IndustryType
}

export enum IndustryType {
  sector = 'sector',
  enabler = 'enabler',
  businessModel = 'business-model',
}

export type OrganizationMatchStatus = { ready: boolean; updatedAt?: string }

export type OrganizationData = Omit<Partial<Organization>, 'id'> & Pick<Organization, 'id'>

export type OrganizationLocator = Pick<Organization, 'name'> & Partial<Pick<Organization, 'domain'>>

export const ORGANIZATIONS_STORE_ID = 'organizations'
